import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Globe2,
  Heart,
  ImageIcon,
  Loader2,
  MapPin,
  MessageCircle,
  Minus,
  Phone,
  Plus,
  Search,
  ShoppingBag,
  Star,
  ThumbsUp,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import '../../styles/PublicMenu/public-menu.css';

const DEFAULT_TEMPLATE_CONFIG = {
  layout: 'classic',
  theme: 'light',
  primary_color: '#51766E',
  background_color: '#F8FAF9',
  text_color: '#333333',
  surface_color: '#FFFFFF',
  accent_color: '#D7E3E0',
  show_search: true,
  show_category_tabs: true,
  show_category_sections: true,
  show_subcategories: true,
  show_featured_section: true,
  show_images: true,
  show_product_description: true,
  show_engagement: true,
  show_restaurant_info: true,
  show_ordering_badge: true,
  product_density: 'comfortable',
  border_radius: 'large',
  card_style: 'elevated',
  card_shadow: 'soft',
  product_image_ratio: '4:3',
  category_style: 'pills',
  logo_position: 'top-right',
  logo_size: 88,
  header_alignment: 'left',
  header_layout: 'classic',
  font_family: 'system',
  heading_scale: 100,
  body_scale: 100,
  content_width: 1080,
  section_gap: 28,
  section_order: ['featured', 'categories', 'products'],
};

const UNAVAILABLE_STATUSES = ['hidden', 'inactive', 'sold_out', 'unavailable'];

export const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

export const translateFallback = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const normalizeTranslations = (translations) => {
  if (Array.isArray(translations)) return translations.filter(Boolean);

  if (translations && typeof translations === 'object') {
    return Object.entries(translations).map(([locale, translation]) => {
      if (!translation || typeof translation !== 'object') {
        return { locale, name: translation ? String(translation) : '' };
      }

      return {
        locale: translation.locale || locale,
        ...translation,
      };
    });
  }

  return [];
};

export const getTranslationValue = (item, field, language, fallback = 'fr') => {
  if (!item) return '';

  if (item?.translations?.[language]?.[field]) {
    return item.translations[language][field];
  }

  if (item?.translations?.[fallback]?.[field]) {
    return item.translations[fallback][field];
  }

  const translations = normalizeTranslations(item.translations);

  const selectedTranslation =
    translations.find((translation) => translation.locale === language) ||
    translations.find((translation) => translation.locale === fallback) ||
    translations.find((translation) => translation.locale === 'en') ||
    translations.find((translation) => translation.locale === 'ar') ||
    translations.find((translation) => translation.locale === 'es') ||
    translations[0];

  return selectedTranslation?.[field] || item?.[field] || '';
};

const getProductSizes = (product) => toArray(product?.sizes);
const getProductSupplements = (product) => toArray(product?.supplements);
const getProductAllergens = (product) => toArray(product?.allergens);
const getProductDietaryLabels = (product) => toArray(product?.dietary_labels);

export const formatPrice = (value, currency = 'MAD') => {
  if (value === null || value === undefined || value === '') return null;
  return `${Number(value).toFixed(2)} ${currency}`;
};

const isVideoUrl = (url = '') => {
  const cleanUrl = String(url).split('?')[0].toLowerCase();

  return (
    cleanUrl.endsWith('.mp4') ||
    cleanUrl.endsWith('.webm') ||
    cleanUrl.endsWith('.mov') ||
    cleanUrl.endsWith('.m4v') ||
    cleanUrl.endsWith('.ogg') ||
    cleanUrl.endsWith('.ogv')
  );
};

const isVideoMedia = (media) => {
  const mediaType = String(
    media?.type ||
      media?.mime_type ||
      media?.media_type ||
      media?.file_type ||
      '',
  ).toLowerCase();

  if (mediaType.startsWith('video/')) return true;
  if (mediaType === 'video' || mediaType === 'movie') return true;

  return isVideoUrl(media?.resolved_url || '');
};

const getProductMedia = (product) => {
  const mediaItems = toArray(product?.images)
    .map((media) => ({
      ...media,
      resolved_url: resolveMediaUrl(
        media?.url ||
          media?.image_url ||
          media?.media_url ||
          media?.video_url ||
          media?.path ||
          media?.image_path ||
          media?.video_path,
      ),
    }))
    .filter((media) => Boolean(media.resolved_url));

  return [...mediaItems].sort((firstMedia, secondMedia) => {
    if (firstMedia.is_primary && !secondMedia.is_primary) return -1;
    if (!firstMedia.is_primary && secondMedia.is_primary) return 1;

    return Number(firstMedia.sort_order || 0) - Number(secondMedia.sort_order || 0);
  });
};

const getProductImage = (product) => {
  const mediaItems = getProductMedia(product);
  const firstImage = mediaItems.find((media) => !isVideoMedia(media)) || mediaItems[0];

  return firstImage?.resolved_url || '';
};

const getProductCurrency = (product, establishment) => {
  return product?.currency || establishment?.currency || 'MAD';
};

const getProductBasePrice = (product, sizeId = '') => {
  const sizes = getProductSizes(product);

  if (sizeId && sizes.length > 0) {
    const selectedSize = sizes.find((size) => Number(size.id) === Number(sizeId));

    if (selectedSize) {
      return Number(selectedSize.discount_price || selectedSize.price || 0);
    }
  }

  return Number(product?.discount_price || product?.base_price || product?.price || 0);
};

const getSupplementPrice = (supplement) => {
  return Number(supplement?.price_override || supplement?.price || supplement?.extra_price || 0);
};

const getProductPrice = (product, t, establishment) => {
  const currency = getProductCurrency(product, establishment);
  const sizes = getProductSizes(product).filter((size) => size.is_active !== false);

  if (product?.pricing_type === 'sizes' && sizes.length > 0) {
    const prices = sizes
      .map((size) => Number(size.discount_price || size.price))
      .filter((price) => !Number.isNaN(price));

    if (prices.length === 0) return t('products.pricing.noPrice');

    return `${t('products.pricing.from')} ${Math.min(...prices).toFixed(2)} ${currency}`;
  }

  if (product?.discount_price) {
    return formatPrice(product.discount_price, currency);
  }

  return formatPrice(product?.base_price || product?.price, currency) || t('products.pricing.noPrice');
};

export const getEngagement = (product) => product?.engagement || {};

const getReviews = (product) => {
  return toArray(product?.reviews || product?.latest_reviews || product?.comments);
};

const isProductAvailable = (product) => {
  return !UNAVAILABLE_STATUSES.includes(String(product?.status || '').toLowerCase());
};

const hexToRgb = (color, fallback = '81, 118, 110') => {
  if (!color) return fallback;

  const trimmed = String(color).trim();

  if (trimmed.startsWith('rgb')) {
    const matches = trimmed.match(/\d+(?:\.\d+)?/g);
    if (matches?.length >= 3) return matches.slice(0, 3).join(', ');
  }

  const normalized = trimmed.replace('#', '');
  const fullHex = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) return fallback;

  const red = parseInt(fullHex.slice(0, 2), 16);
  const green = parseInt(fullHex.slice(2, 4), 16);
  const blue = parseInt(fullHex.slice(4, 6), 16);

  return `${red}, ${green}, ${blue}`;
};

function VideoThumbnail({ src }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return undefined;

    const showPreviewFrame = () => {
      try {
        const previewTime = Math.min(Math.max(Number(video.duration || 0) * 0.08, 0.12), 1);

        if (Number.isFinite(previewTime)) {
          video.currentTime = previewTime;
        }
      } catch {
        // Keep the browser video preview if seeking is unavailable.
      }
    };

    video.addEventListener('loadedmetadata', showPreviewFrame);

    return () => {
      video.removeEventListener('loadedmetadata', showPreviewFrame);
    };
  }, [src]);

  return (
    <span className="public-product-media-slider__video-thumb">
      <video ref={videoRef} src={src} muted playsInline preload="metadata" aria-hidden="true" />
      <span className="public-product-media-slider__video-thumb-play">▶</span>
    </span>
  );
}

function ProductCard({ product, language, fallbackLanguage, establishment, effectiveConfig, t, onClick }) {
  const imageUrl = getProductImage(product);
  const productName = getTranslationValue(product, 'name', language, fallbackLanguage);
  const productDescription = getTranslationValue(product, 'description', language, fallbackLanguage);
  const engagement = getEngagement(product);
  const sizes = getProductSizes(product);
  const supplements = getProductSupplements(product);
  const available = isProductAvailable(product);

  return (
    <button
      type="button"
      className={[
        'public-menu-product',
        !available ? 'public-menu-product--unavailable' : '',
        effectiveConfig.show_images === false
          ? 'public-menu-product--no-image'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
    >
      {effectiveConfig.show_images !== false && (
        <div className="public-menu-product__image">
          {imageUrl ? <img src={imageUrl} alt={productName} /> : <ImageIcon size={24} />}
        </div>
      )}

      <div className="public-menu-product__content">
        <div className="public-menu-product__top">
          <strong>{productName || t('menuDesign.menuItems')}</strong>
          <span>{getProductPrice(product, t, establishment)}</span>
        </div>

        {effectiveConfig.show_product_description !== false && productDescription && <p>{productDescription}</p>}

        {effectiveConfig.show_engagement !== false && (
          <div className="public-menu-product__engagement">
            <span>
              <Star size={14} />
              {Number(engagement.rating_average || 0).toFixed(1)}
            </span>
            <span>
              <Heart size={14} />
              {engagement.likes_count || 0}
            </span>
            <span>
              <MessageCircle size={14} />
              {engagement.reviews_count || 0}
            </span>
          </div>
        )}

        <div className="public-menu-product__chips">
          {!available && <small>{t('publicMenu.productModal.unavailable')}</small>}

          {sizes
            .filter((size) => size.is_active !== false)
            .slice(0, 4)
            .map((size) => (
              <small key={size.id}>{getTranslationValue(size, 'name', language, fallbackLanguage)}</small>
            ))}
        </div>

        {product?.allow_supplements && supplements.length > 0 && (
          <div className="public-menu-product__addons">
            + {supplements.length} {t('menuDesign.addons')}
          </div>
        )}
      </div>
    </button>
  );
}

function ProductModal({
  product,
  establishment,
  language,
  fallbackLanguage,
  t,
  liked,
  detailsUrl,
  detailsLabel,
  onClose,
  onLike,
  onReview,
  onOrderNow,
  orderingEnabled,
  previewMode,
}) {
  const sizes = getProductSizes(product).filter((size) => size.is_active !== false);
  const supplements = getProductSupplements(product).filter((supplement) => supplement.is_active !== false);
  const allergens = getProductAllergens(product);
  const dietaryLabels = getProductDietaryLabels(product);
  const engagement = getEngagement(product);
  const currency = getProductCurrency(product, establishment);
  const available = isProductAvailable(product);
  const canOrder = Boolean(orderingEnabled) && available;
  const defaultSize = sizes.find((size) => size.is_default) || sizes[0] || null;

  const [selectedSizeId, setSelectedSizeId] = useState(defaultSize?.id ? String(defaultSize.id) : '');
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [itemNote, setItemNote] = useState('');
  const [localReviews, setLocalReviews] = useState(() => getReviews(product));
  const [reviewForm, setReviewForm] = useState({ customer_name: '', rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const imageTouchStartX = useRef(null);

  const productMedia = useMemo(() => getProductMedia(product), [product]);

  useEffect(() => {
    setLocalReviews(getReviews(product));
  }, [product]);

  useEffect(() => {
    setActiveImageIndex(0);
    setSelectedSizeId(defaultSize?.id ? String(defaultSize.id) : '');
    setSelectedSupplements([]);
    setQuantity(1);
    setItemNote('');
  }, [product?.id]);

  const productName = getTranslationValue(product, 'name', language, fallbackLanguage);
  const productDescription = getTranslationValue(product, 'description', language, fallbackLanguage);
  const ingredients = getTranslationValue(product, 'ingredients', language, fallbackLanguage);
  const imageUrl = getProductImage(product);
  const activeMedia = productMedia[activeImageIndex] || null;
  const activeMediaIsVideo = isVideoMedia(activeMedia);
  const hasMultipleMedia = productMedia.length > 1;

  const showPreviousImage = () => {
    if (!hasMultipleMedia) return;

    setActiveImageIndex((currentIndex) =>
      currentIndex === 0 ? productMedia.length - 1 : currentIndex - 1,
    );
  };

  const showNextImage = () => {
    if (!hasMultipleMedia) return;

    setActiveImageIndex((currentIndex) =>
      currentIndex === productMedia.length - 1 ? 0 : currentIndex + 1,
    );
  };

  const handleImageTouchStart = (event) => {
    if (activeMediaIsVideo) {
      imageTouchStartX.current = null;
      return;
    }

    imageTouchStartX.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleImageTouchEnd = (event) => {
    if (imageTouchStartX.current === null) return;

    const touchEndX = event.changedTouches?.[0]?.clientX ?? imageTouchStartX.current;
    const distance = touchEndX - imageTouchStartX.current;

    imageTouchStartX.current = null;

    if (Math.abs(distance) < 45) return;
    if (distance > 0) showPreviousImage();
    else showNextImage();
  };

  const unitPrice = useMemo(() => {
    const basePrice = getProductBasePrice(product, selectedSizeId);
    const addOnsPrice = supplements
      .filter((supplement) => selectedSupplements.includes(String(supplement.id)))
      .reduce((total, supplement) => total + getSupplementPrice(supplement), 0);

    return basePrice + addOnsPrice;
  }, [product, selectedSizeId, selectedSupplements, supplements]);

  const totalPrice = unitPrice * quantity;
  const selectedSize = sizes.find((size) => String(size.id) === String(selectedSizeId));

  const visibleReviews = localReviews.slice(0, 8);
  const reviewsCount = Math.max(Number(engagement.reviews_count || 0), localReviews.length);

  const toggleSupplement = (supplementId) => {
    const stringId = String(supplementId);

    setSelectedSupplements((currentIds) =>
      currentIds.includes(stringId)
        ? currentIds.filter((id) => id !== stringId)
        : [...currentIds, stringId],
    );
  };

  const submitReview = async (event) => {
    event.preventDefault();

    const customerName = reviewForm.customer_name.trim();
    const comment = reviewForm.comment.trim();

    if (!customerName || !comment) return;

    try {
      setSubmittingReview(true);

      if (onReview) {
        await onReview(product.id, {
          customer_name: customerName,
          rating: Number(reviewForm.rating || 5),
          comment,
        });
      }

      setLocalReviews((currentReviews) => [
        {
          id: `local-${Date.now()}`,
          customer_name: customerName,
          rating: Number(reviewForm.rating || 5),
          comment,
          created_at: new Date().toISOString(),
          is_local: true,
        },
        ...currentReviews,
      ]);

      setReviewForm({ customer_name: '', rating: 5, comment: '' });
    } finally {
      setSubmittingReview(false);
    }
  };

  const buildCartItem = () => {
    const selectedSupplementsData = supplements.filter((supplement) =>
      selectedSupplements.includes(String(supplement.id)),
    );

    return {
      row_id: `${product.id}-${Date.now()}-${Math.random()}`,
      product_id: product.id,
      product_size_id: selectedSizeId || null,
      supplement_ids: selectedSupplementsData.map((supplement) => supplement.id),
      quantity,
      note: itemNote.trim() || null,
      unit_price: unitPrice,
      total_price: totalPrice,
      product_snapshot: {
        name: productName,
        image: imageUrl,
        currency,
        size_name: selectedSize ? getTranslationValue(selectedSize, 'name', language, fallbackLanguage) : '',
        supplements: selectedSupplementsData.map((supplement) => ({
          id: supplement.id,
          name: getTranslationValue(supplement, 'name', language, fallbackLanguage),
          price: getSupplementPrice(supplement),
        })),
      },
    };
  };

  const orderNow = () => {
    if (!canOrder) return;
    onOrderNow?.(buildCartItem());
  };

  const detailsText = detailsLabel || translateFallback(t, 'publicMenu.productModal.fullDetails', 'Voir tous les détails');
  const canShowReviews = previewMode || establishment?.allow_ratings || establishment?.allow_comments;

  const detailsAction = detailsUrl ? (
    <Link to={detailsUrl} className="public-product-details-link">
      {detailsText}
    </Link>
  ) : (
    <button type="button" className="public-product-details-link" onClick={() => onClose('details')}>
      {detailsText}
    </button>
  );

  return (
    <div className="public-product-modal" role="dialog" aria-modal="true">
      <button type="button" className="public-product-modal__overlay" onClick={onClose} aria-label={t('common.close')} />

      <section className="public-product-modal__content public-product-modal__content--modern">
        <div
          className="public-product-modal__image public-product-modal__image--slider"
          onTouchStart={handleImageTouchStart}
          onTouchEnd={handleImageTouchEnd}
        >
          <div className="public-product-media-slider__stage">
            {activeMedia?.resolved_url ? (
              activeMediaIsVideo ? (
                <video
                  key={activeMedia.id || activeMedia.resolved_url}
                  src={activeMedia.resolved_url}
                  controls
                  playsInline
                  preload="metadata"
                >
                  <track kind="captions" />
                </video>
              ) : (
                <img
                  key={activeMedia.id || activeMedia.resolved_url}
                  src={activeMedia.resolved_url}
                  alt={`${productName} ${activeImageIndex + 1}`}
                />
              )
            ) : (
              <ImageIcon size={34} />
            )}
          </div>

          {(activeMedia?.resolved_url || !productMedia.length) && (
            <div className={`public-product-media-slider__type ${activeMediaIsVideo ? 'public-product-media-slider__type--video' : ''}`}>
              {activeMediaIsVideo ? (
                <>
                  <span className="public-product-media-slider__play-dot">▶</span>
                  {translateFallback(t, 'publicMenu.productModal.video', 'Video')}
                </>
              ) : (
                <>
                  <ImageIcon size={14} />
                  {translateFallback(t, 'publicMenu.productModal.photo', 'Photo')}
                </>
              )}
            </div>
          )}

          {hasMultipleMedia && (
            <>
              <button
                type="button"
                className="public-product-image-slider__arrow public-product-image-slider__arrow--previous"
                onClick={showPreviousImage}
                aria-label={translateFallback(t, 'publicMenu.productModal.previousMedia', 'Previous media')}
              >
                <ChevronLeft size={22} />
              </button>

              <button
                type="button"
                className="public-product-image-slider__arrow public-product-image-slider__arrow--next"
                onClick={showNextImage}
                aria-label={translateFallback(t, 'publicMenu.productModal.nextMedia', 'Next media')}
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          <div className="public-product-media-slider__footer">
            <div className="public-product-image-slider__counter">
              {Math.min(activeImageIndex + 1, Math.max(productMedia.length, 1))} / {Math.max(productMedia.length, 1)}
            </div>

            {hasMultipleMedia && (
              <div className="public-product-media-slider__thumbnails" aria-label={translateFallback(t, 'publicMenu.productModal.media', 'Product media')}>
                {productMedia.map((media, index) => {
                  const mediaIsVideo = isVideoMedia(media);

                  return (
                    <button
                      key={media.id || media.resolved_url}
                      type="button"
                      className={activeImageIndex === index ? 'active' : ''}
                      onClick={() => setActiveImageIndex(index)}
                      aria-label={`${translateFallback(
                        t,
                        mediaIsVideo ? 'publicMenu.productModal.video' : 'publicMenu.productModal.photo',
                        mediaIsVideo ? 'Video' : 'Photo',
                      )} ${index + 1}`}
                      aria-current={activeImageIndex === index ? 'true' : undefined}
                    >
                      {mediaIsVideo ? <VideoThumbnail src={media.resolved_url} /> : <img src={media.resolved_url} alt="" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="public-product-modal__body">
          <div className="public-product-modal__topbar" style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: 12 }}>
            <button
              type="button"
              className="public-product-modal__close"
              onClick={onClose}
              aria-label={t('common.close')}
              style={{ position: 'static', inset: 'auto', flexShrink: 0, width: 38, height: 38 }}
            >
              <X size={20} />
            </button>
          </div>
          <div className="public-product-modal__head">
            <div>
              <h2>{productName}</h2>
              {productDescription && <p>{productDescription}</p>}
            </div>

            <strong>{getProductPrice(product, t, establishment)}</strong>
          </div>

          <div className="public-product-engagement">
            <span>
              <Star size={15} />
              {Number(engagement.rating_average || 0).toFixed(1)}
            </span>
            <span>
              <MessageCircle size={15} />
              {reviewsCount}
            </span>
            <button type="button" onClick={() => onLike?.(product.id)}>
              <Heart size={15} fill={liked ? 'currentColor' : 'none'} />
              {engagement.likes_count || 0}
            </button>
          </div>

          <div className="public-product-meta-list">
            <span className={available ? 'is-available' : 'is-unavailable'}>
              {available ? t('publicMenu.productModal.available') : t('publicMenu.productModal.unavailable')}
            </span>
          </div>

          {ingredients && (
            <div className="public-product-info-block">
              <h3>{t('publicMenu.productModal.ingredients')}</h3>
              <p>{ingredients}</p>
            </div>
          )}

          {allergens.length > 0 && (
            <div className="public-product-info-block">
              <h3>{t('publicMenu.productModal.allergens')}</h3>
              <div className="public-product-info-tags">
                {allergens.map((allergen) => (
                  <span key={allergen.id || getTranslationValue(allergen, 'name', language, fallbackLanguage)}>
                    {getTranslationValue(allergen, 'name', language, fallbackLanguage) || allergen.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {dietaryLabels.length > 0 && (
            <div className="public-product-info-block">
              <h3>{t('publicMenu.productModal.dietaryLabels')}</h3>
              <div className="public-product-info-tags">
                {dietaryLabels.map((label) => (
                  <span key={label.id || getTranslationValue(label, 'name', language, fallbackLanguage)}>
                    {getTranslationValue(label, 'name', language, fallbackLanguage) || label.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {sizes.length > 0 && (
            <div className="public-product-options">
              <h3>{t('publicMenu.productModal.sizes')}</h3>
              <div className="public-product-options__grid">
                {sizes.map((size) => {
                  const sizeName = getTranslationValue(size, 'name', language, fallbackLanguage);
                  const active = String(size.id) === String(selectedSizeId);

                  return (
                    <button key={size.id} type="button" className={active ? 'active' : ''} onClick={() => setSelectedSizeId(String(size.id))}>
                      <span>{sizeName}</span>
                      <strong>{formatPrice(size.discount_price || size.price, currency)}</strong>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {supplements.length > 0 && (
            <div className="public-product-options">
              <h3>{t('publicMenu.productModal.addons')}</h3>
              <div className="public-product-options__grid">
                {supplements.map((supplement) => {
                  const supplementName = getTranslationValue(supplement, 'name', language, fallbackLanguage);
                  const active = selectedSupplements.includes(String(supplement.id));

                  return (
                    <button key={supplement.id} type="button" className={active ? 'active' : ''} onClick={() => toggleSupplement(supplement.id)}>
                      <span>{supplementName}</span>
                      <strong>+ {formatPrice(getSupplementPrice(supplement), currency)}</strong>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {orderingEnabled && available ? (
            <div className="public-product-order-box public-product-order-box--modern">
              <div className="public-product-quantity">
                <button type="button" onClick={() => setQuantity((current) => Math.max(1, current - 1))}>
                  <Minus size={15} />
                </button>

                <strong>{quantity}</strong>

                <button type="button" onClick={() => setQuantity((current) => current + 1)}>
                  <Plus size={15} />
                </button>
              </div>

              <input
                value={itemNote}
                placeholder={t('publicMenu.productModal.itemNote')}
                onChange={(event) => setItemNote(event.target.value)}
              />

              <div className="public-product-order-actions public-product-order-actions--two">
                <button type="button" className="public-product-order-button" onClick={orderNow}>
                  <ShoppingBag size={17} />
                  {`${translateFallback(t, 'publicMenu.productModal.orderNow', 'Commander maintenant')} · ${formatPrice(totalPrice, currency)}`}
                </button>

                {detailsAction}
              </div>
            </div>
          ) : (
            <div className="public-product-details-only">{detailsAction}</div>
          )}

          {canShowReviews && (
            <form className="public-product-review-form" onSubmit={submitReview}>
              <h3>{t('publicMenu.productModal.leaveReview')}</h3>

              <input
                value={reviewForm.customer_name}
                placeholder={t('publicMenu.productModal.namePlaceholder')}
                onChange={(event) =>
                  setReviewForm((current) => ({ ...current, customer_name: event.target.value }))
                }
              />

              {(previewMode || establishment?.allow_ratings) && (
                <div className="public-product-review-stars">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      className={reviewForm.rating >= rating ? 'active' : ''}
                      onClick={() => setReviewForm((current) => ({ ...current, rating }))}
                    >
                      <Star size={18} fill="currentColor" />
                    </button>
                  ))}
                </div>
              )}

              {(previewMode || establishment?.allow_comments) && (
                <textarea
                  value={reviewForm.comment}
                  placeholder={t('publicMenu.productModal.commentPlaceholder')}
                  onChange={(event) => setReviewForm((current) => ({ ...current, comment: event.target.value }))}
                />
              )}

              <button type="submit" disabled={submittingReview}>
                {submittingReview ? <Loader2 size={17} /> : <ThumbsUp size={17} />}
                {t('publicMenu.productModal.sendReview')}
              </button>
            </form>
          )}

          <div className="public-product-reviews public-product-reviews--modern">
            <h3>{t('publicMenu.productModal.reviews')}</h3>

            {visibleReviews.length === 0 ? (
              <div className="public-product-review-empty">
                <MessageCircle size={22} />
                <p>{translateFallback(t, 'publicMenu.productModal.noReviews', 'Aucun commentaire pour le moment.')}</p>
              </div>
            ) : (
              visibleReviews.map((review, index) => (
                <article key={review.id || index}>
                  <div className="public-product-review-head">
                    <strong>{review.customer_name || t('publicMenu.anonymous')}</strong>
                    <span>
                      <Star size={14} fill="currentColor" />
                      {review.rating || 5}
                    </span>
                  </div>

                  {review.comment && <p>{review.comment}</p>}

                  {review.is_local && <small>{translateFallback(t, 'publicMenu.productModal.justAdded', 'Ajouté maintenant')}</small>}
                </article>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const LANGUAGE_OPTIONS = [
  { value: 'fr', label: 'Français', helper: 'Menu en français' },
  { value: 'ar', label: 'العربية', helper: 'القائمة بالعربية' },
  { value: 'en', label: 'English', helper: 'Menu in English' },
  { value: 'es', label: 'Español', helper: 'Menú en español' },
  { value: 'de', label: 'Deutsch', helper: 'Menü auf Deutsch' },
  { value: 'zh', label: '中文', helper: '中文菜单' },
  { value: 'ru', label: 'Русский', helper: 'Меню на русском' },
  { value: 'nl', label: 'Nederlands', helper: 'Menu in het Nederlands' },
  { value: 'ja', label: '日本語', helper: '日本語のメニュー' },
  { value: 'it', label: 'Italiano', helper: 'Menù in italiano' },
  { value: 'pt', label: 'Português', helper: 'Menu em português' },
  { value: 'tr', label: 'Türkçe', helper: 'Türkçe menü' },
];

const getEnabledLanguageOptions = (menu) => {
  const raw = menu?.enabled_languages ?? menu?.selected_languages ??
    menu?.available_languages ?? menu?.settings?.enabled_languages ??
    menu?.settings?.selected_languages ?? menu?.settings?.available_languages;
  // Older API responses may not include enabled_languages.
  if (raw === undefined || raw === null || raw === '') return LANGUAGE_OPTIONS;
  let parsed = raw;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); }
    catch { parsed = parsed.split(',').map((locale) => locale.trim()); }
  }
  if (parsed && !Array.isArray(parsed) && typeof parsed === 'object') {
    parsed = Object.keys(parsed).filter((locale) => Boolean(parsed[locale]));
  }
  if (!Array.isArray(parsed)) return LANGUAGE_OPTIONS;
  const enabled = new Set(parsed.map((locale) => String(locale).toLowerCase()));
  enabled.add(LANGUAGE_OPTIONS.some((option) => option.value === menu?.default_language)
    ? menu.default_language : 'en');
  return LANGUAGE_OPTIONS.filter((option) => enabled.has(option.value));
};

function LanguagePicker({
  language,
  options = LANGUAGE_OPTIONS,
  onLanguageChange,
  compact = false,
  open: controlledOpen,
  onToggle,
  renderMenu = true,
  buttonRef = null,
}) {
  const [localOpen, setLocalOpen] = useState(false);
  const pickerRef = useRef(null);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : localOpen;

  const selectedLanguage = options.find((option) => option.value === language) || options[0];

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = (event) => {
      if (!pickerRef.current?.contains(event.target)) {
        if (isControlled) onToggle?.(false);
        else setLocalOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (isControlled) onToggle?.(false);
        else setLocalOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, isControlled, onToggle]);

  const setOpen = (nextValue) => {
    if (isControlled) {
      onToggle?.(nextValue);
      return;
    }

    setLocalOpen(nextValue);
  };

  const selectLanguage = (languageCode) => {
    onLanguageChange?.(languageCode);
    setOpen(false);
  };

  return (
    <div ref={pickerRef} className={`public-menu-language-select public-menu-language-select--custom ${open ? 'is-open' : ''} ${compact ? 'is-compact' : ''}`}>
      <button
        ref={buttonRef}
        type="button"
        className="public-menu-language-select__button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <Globe2 size={16} />
        <span>
          <strong>{selectedLanguage.label}</strong>
          <small>{selectedLanguage.helper}</small>
        </span>
        <ChevronDown size={16} />
      </button>

      {renderMenu && open && (
        <div className="public-menu-language-select__options">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={language === option.value ? 'active' : ''}
              onClick={() => selectLanguage(option.value)}
            >
              <span>
                <strong>{option.label}</strong>
                <small>{option.helper}</small>
              </span>
              {language === option.value && <CheckCircle2 size={16} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewScreen({ title, children, onBack }) {
  return (
    <section className="public-menu-preview-screen">
      <button type="button" className="public-menu-preview-screen__back" onClick={onBack}>
        <ChevronLeft size={16} />
        Back
      </button>

      <h2>{title}</h2>
      {children}
    </section>
  );
}

export default function PublicMenuExperience({
  mode = 'public',
  phonePreview = false,
  menu = null,
  establishment = null,
  categories = [],
  products = [],
  featuredProducts = [],
  template = null,
  designSettings = {},
  orderingEnabled = false,
  language: controlledLanguage,
  onLanguageChange,
  likedProductIds = [],
  cartCount = 0,
  cartTotal = 0,
  onProductView,
  onCategoryChange,
  onCheckoutClick,
  onOrdersClick,
  onLike,
  onReview,
  onOrderNow,
  detailsUrlForProduct,
  externalSearchTerm = '',
  showFloatingCart = false,
  className = '',
}) {
  const { t, language: contextLanguage, changeLanguage } = useLanguage();
  const previewMode = mode === 'preview';
  const enabledLanguageOptions = useMemo(() => getEnabledLanguageOptions(menu), [menu]);
  const requestedLanguage = controlledLanguage || contextLanguage;
  const fallbackLanguage = menu?.default_language || establishment?.default_language || 'en';
  const language = enabledLanguageOptions.some((option) => option.value === requestedLanguage)
    ? requestedLanguage
    : (enabledLanguageOptions.find((option) => option.value === fallbackLanguage)?.value || enabledLanguageOptions[0]?.value || 'en');
  const experienceRef = useRef(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [previewLikedProductIds, setPreviewLikedProductIds] = useState([]);
  const [previewCartItems, setPreviewCartItems] = useState([]);
  const [notice, setNotice] = useState('');
  const [previewScreen, setPreviewScreen] = useState('menu');

  const normalizedCategories = useMemo(() => toArray(categories), [categories]);
  const normalizedProducts = useMemo(() => toArray(products), [products]);

  const effectiveConfig = useMemo(
    () => ({
      ...DEFAULT_TEMPLATE_CONFIG,
      ...(template?.config || {}),
      ...(establishment?.menu_design_settings || {}),
      ...(menu?.design_settings || {}),
      ...(menu?.menu_design_settings || {}),
      ...(designSettings || {}),
    }),
    [template?.config, establishment?.menu_design_settings, menu?.design_settings, menu?.menu_design_settings, designSettings],
  );

  const normalizedSectionOrder = useMemo(() => {
    const requestedOrder = Array.isArray(effectiveConfig.section_order) ? effectiveConfig.section_order : [];
    const supportedSections = ['featured', 'categories', 'products'];
    const uniqueOrder = requestedOrder.filter(
      (section, index, array) => supportedSections.includes(section) && array.indexOf(section) === index,
    );

    supportedSections.forEach((section) => {
      if (!uniqueOrder.includes(section)) uniqueOrder.push(section);
    });

    return uniqueOrder;
  }, [effectiveConfig.section_order]);

  const activeSearchTerm = String(externalSearchTerm || searchTerm || '').trim().toLowerCase();

  const selectedCategoryIds = useMemo(() => {
    if (!selectedCategoryId) return [];

    const selectedCategory = normalizedCategories.find((category) => Number(category.id) === Number(selectedCategoryId));

    return [
      Number(selectedCategoryId),
      ...toArray(selectedCategory?.children).map((childCategory) => Number(childCategory.id)),
    ];
  }, [normalizedCategories, selectedCategoryId]);

  const searchFilteredProducts = useMemo(() => {
    return normalizedProducts.filter((product) => {
      const name = getTranslationValue(product, 'name', language, fallbackLanguage);
      const description = getTranslationValue(product, 'description', language, fallbackLanguage);

      return (
        !activeSearchTerm ||
        name.toLowerCase().includes(activeSearchTerm) ||
        description.toLowerCase().includes(activeSearchTerm)
      );
    });
  }, [normalizedProducts, activeSearchTerm, language, fallbackLanguage]);

  const filteredProducts = useMemo(() => {
    if (selectedCategoryIds.length === 0) return searchFilteredProducts;

    return searchFilteredProducts.filter((product) => selectedCategoryIds.includes(Number(product.category_id)));
  }, [searchFilteredProducts, selectedCategoryIds]);

  const visibleCategorySections = useMemo(() => {
    const productMatchesSearch = (product) => {
      return searchFilteredProducts.some((filteredProduct) => Number(filteredProduct.id) === Number(product.id));
    };

    return normalizedCategories
      .map((category) => {
        if (selectedCategoryId && Number(selectedCategoryId) !== Number(category.id)) return null;

        const categoryProducts = toArray(category.products);
        const directProducts = categoryProducts.length > 0
          ? categoryProducts.filter(productMatchesSearch)
          : searchFilteredProducts.filter((product) => Number(product.category_id) === Number(category.id));

        const children = toArray(category.children)
          .map((childCategory) => {
            const childCategoryProducts = toArray(childCategory.products);
            const childProducts = childCategoryProducts.length > 0
              ? childCategoryProducts.filter(productMatchesSearch)
              : searchFilteredProducts.filter((product) => Number(product.category_id) === Number(childCategory.id));

            return { ...childCategory, products: childProducts };
          })
          .filter((childCategory) => childCategory.products.length > 0);

        if (directProducts.length === 0 && children.length === 0) return null;

        return { ...category, products: directProducts, children };
      })
      .filter(Boolean);
  }, [normalizedCategories, searchFilteredProducts, selectedCategoryId]);

  const uncategorizedProducts = useMemo(() => {
    const knownCategoryIds = normalizedCategories.flatMap((category) => [
      Number(category.id),
      ...toArray(category.children).map((childCategory) => Number(childCategory.id)),
    ]);

    return filteredProducts.filter((product) => !knownCategoryIds.includes(Number(product.category_id)));
  }, [normalizedCategories, filteredProducts]);

  const visibleFeaturedProducts = useMemo(() => {
    const apiFeaturedProducts = toArray(featuredProducts);

    if (apiFeaturedProducts.length > 0) return apiFeaturedProducts.slice(0, 10);

    return normalizedProducts.filter((product) => product.is_featured !== false).slice(0, 10);
  }, [featuredProducts, normalizedProducts]);

  const previewCartCount = previewCartItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
  const previewCartTotal = previewCartItems.reduce((total, item) => total + Number(item.total_price || 0), 0);
  const effectiveCartCount = previewMode ? previewCartCount : cartCount;
  const effectiveCartTotal = previewMode ? previewCartTotal : cartTotal;
  const effectiveLikedProductIds = previewMode ? previewLikedProductIds : likedProductIds;

  const shouldShowCategorySections = effectiveConfig.show_category_sections !== false;
  const shouldShowSubcategories = effectiveConfig.show_subcategories !== false;
  const hasStructuredProducts = visibleCategorySections.length > 0 || uncategorizedProducts.length > 0;
  const establishmentName = establishment?.name || t('menuDesign.restaurantName');
  const menuName = menu?.name || t('menuDesign.digitalMenu');
  const currency = establishment?.currency || 'MAD';
  const isOrderingEnabled = Boolean(orderingEnabled);

  const showNotice = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 1800);
  };

  const changeActiveLanguage = (languageCode) => {
    if (!enabledLanguageOptions.some((option) => option.value === languageCode)) return;
    if (onLanguageChange) {
      onLanguageChange(languageCode);
    } else {
      changeLanguage(languageCode);
    }

  };

  const openProduct = (product) => {
    setSelectedProduct(product);
    onProductView?.(product);
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    onCategoryChange?.(categoryId);
  };

  const handleLike = async (productId) => {
    if (onLike) {
      await onLike(productId);
      return;
    }

    setPreviewLikedProductIds((currentIds) =>
      currentIds.includes(Number(productId))
        ? currentIds.filter((id) => Number(id) !== Number(productId))
        : [...currentIds, Number(productId)],
    );
  };

  const handleOrderNow = (item) => {
    if (onOrderNow) {
      onOrderNow(item);
      return;
    }

    setPreviewCartItems((currentItems) => [...currentItems, item]);
    setSelectedProduct(null);
    showNotice(translateFallback(t, 'publicMenu.cart.added', 'Added to preview cart'));
  };

  const handleCheckoutClick = () => {
    if (onCheckoutClick) {
      onCheckoutClick();
      return;
    }

    if (previewMode) {
      setPreviewScreen('checkout');
      return;
    }

    showNotice(translateFallback(t, 'publicMenu.cart.checkout', 'Checkout'));
  };

  const handleOrdersClick = () => {
    if (onOrdersClick) {
      onOrdersClick();
      return;
    }

    if (previewMode) {
      setPreviewScreen('orders');
      return;
    }

    showNotice(translateFallback(t, 'publicMenu.customerOrders.title', 'My orders'));
  };

  const buildStyle = {
    '--public-menu-primary': effectiveConfig.primary_color || '#51766E',
    '--public-menu-bg': effectiveConfig.background_color || '#F8FAF9',
    '--public-menu-text': effectiveConfig.text_color || '#333333',
    '--public-menu-surface': effectiveConfig.surface_color || '#FFFFFF',
    '--public-menu-accent': effectiveConfig.accent_color || '#D7E3E0',
    '--public-menu-primary-rgb': hexToRgb(effectiveConfig.primary_color, '81, 118, 110'),
    '--public-menu-bg-rgb': hexToRgb(effectiveConfig.background_color, '248, 250, 249'),
    '--public-menu-text-rgb': hexToRgb(effectiveConfig.text_color, '51, 51, 51'),
    '--public-menu-surface-rgb': hexToRgb(effectiveConfig.surface_color, '255, 255, 255'),
    '--public-menu-accent-rgb': hexToRgb(effectiveConfig.accent_color, '215, 227, 224'),
    '--public-menu-content-width': `${Number(effectiveConfig.content_width || 1080)}px`,
    '--public-menu-preview-content-width': `${
      94 +
      ((Math.min(
        1280,
        Math.max(
          720,
          Number(effectiveConfig.content_width || 1080),
        ),
      ) -
        720) /
        560) *
        6
    }%`,
    '--public-menu-section-gap': `${Number(effectiveConfig.section_gap || 28)}px`,
    '--public-menu-logo-size': `${Number(effectiveConfig.logo_size || 88)}px`,
    '--public-menu-heading-scale': Number(effectiveConfig.heading_scale || 100) / 100,
    '--public-menu-body-scale': Number(effectiveConfig.body_scale || 100) / 100,
    '--public-menu-hero-title-size': `${Math.round(52 * (Number(effectiveConfig.heading_scale || 100) / 100))}px`,
    '--public-menu-phone-title-size': `${Math.round(30 * (Number(effectiveConfig.heading_scale || 100) / 100))}px`,
    '--public-menu-section-title-size': `${Math.round(23 * (Number(effectiveConfig.heading_scale || 100) / 100))}px`,
    '--public-product-modal-title-size': `${Math.round(34 * (Number(effectiveConfig.heading_scale || 100) / 100))}px`,
  };

  const rootClassName = [
    'public-menu',
    'public-menu--shared-experience',
    previewMode ? 'public-menu--preview-mode' : 'public-menu--public-mode',
    phonePreview ? 'public-menu--phone-preview' : '',
    selectedProduct ? 'public-menu--modal-open' : '',
    isOrderingEnabled ? 'public-menu--orders-enabled' : 'public-menu--orders-disabled',
    `public-menu--${effectiveConfig.layout || 'classic'}`,
    `public-menu--${effectiveConfig.theme || 'light'}`,
    `public-menu--${effectiveConfig.product_density || 'comfortable'}`,
    `public-menu--radius-${effectiveConfig.border_radius || 'large'}`,
    `public-menu--card-${effectiveConfig.card_style || 'elevated'}`,
    `public-menu--shadow-${effectiveConfig.card_shadow || 'soft'}`,
    `public-menu--image-${String(effectiveConfig.product_image_ratio || '4:3').replace(':', '-')}`,
    `public-menu--category-${effectiveConfig.category_style || 'pills'}`,
    `public-menu--font-${effectiveConfig.font_family || 'system'}`,
    `public-menu--logo-${effectiveConfig.logo_position || 'top-right'}`,
    `public-menu--header-${effectiveConfig.header_layout || 'classic'}`,
    `public-menu--align-${effectiveConfig.header_alignment || 'left'}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const renderCategoryTabs = () => {
    if (effectiveConfig.show_category_tabs === false || normalizedCategories.length === 0) return null;

    return (
      <div className="public-menu__categories">
        <button type="button" className={!selectedCategoryId ? 'active' : ''} onClick={() => handleCategoryChange('')}>
          {t('common.all')}
        </button>

        {normalizedCategories.map((category) => (
          <button
            key={category.id}
            type="button"
            className={Number(selectedCategoryId) === Number(category.id) ? 'active' : ''}
            onClick={() => handleCategoryChange(String(category.id))}
          >
            {getTranslationValue(category, 'name', language, fallbackLanguage)}
          </button>
        ))}
      </div>
    );
  };

  const renderFeaturedSection = () => {
    if (effectiveConfig.show_featured_section === false || visibleFeaturedProducts.length === 0) return null;

    return (
      <section className="public-menu__section public-menu__section--featured">
        <div className="public-menu__section-title">
          <Star size={18} />
          <h2>{translateFallback(t, 'menuDesign.specialDishes', 'Special dishes')}</h2>
        </div>

        <div className="public-menu__featured">
          {visibleFeaturedProducts.map((product) => {
            const imageUrl = getProductImage(product);
            const productName = getTranslationValue(product, 'name', language, fallbackLanguage);

            return (
              <button
                key={product.id}
                type="button"
                className={
                  effectiveConfig.show_images === false
                    ? 'public-menu-featured-card--no-image'
                    : ''
                }
                onClick={() => openProduct(product)}
              >
                {effectiveConfig.show_images !== false && (imageUrl ? <img src={imageUrl} alt={productName} /> : <span><ImageIcon size={22} /></span>)}

                <div>
                  <strong>{productName}</strong>
                  <small>{getProductPrice(product, t, establishment)}</small>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  const renderProductCard = (product) => (
    <ProductCard
      key={product.id}
      product={product}
      language={language}
      fallbackLanguage={fallbackLanguage}
      establishment={establishment}
      effectiveConfig={effectiveConfig}
      t={t}
      onClick={() => openProduct(product)}
    />
  );

  const renderProductsSection = () => {
    if (shouldShowCategorySections) {
      if (!hasStructuredProducts) {
        return (
          <section className="public-menu__section">
            <div className="public-menu__empty">
              <UtensilsCrossed size={28} />
              <p>{t('menuDesign.noProducts')}</p>
            </div>
          </section>
        );
      }

      return (
        <>
          {visibleCategorySections.map((category) => (
            <section key={category.id} className="public-menu-category-section">
              <div className="public-menu__section-title">
                <UtensilsCrossed size={18} />
                <h2>{getTranslationValue(category, 'name', language, fallbackLanguage)}</h2>
              </div>

              {getTranslationValue(category, 'description', language, fallbackLanguage) && (
                <p className="public-menu-category-section__description">
                  {getTranslationValue(category, 'description', language, fallbackLanguage)}
                </p>
              )}

              {toArray(category.products).length > 0 && (
                <div className="public-menu__products">
                  {toArray(category.products).map(renderProductCard)}
                </div>
              )}

              {shouldShowSubcategories &&
                toArray(category.children).map((childCategory) => (
                  <div key={childCategory.id} className="public-menu-subcategory-section">
                    <h3>{getTranslationValue(childCategory, 'name', language, fallbackLanguage)}</h3>
                    <div className="public-menu__products">
                      {toArray(childCategory.products).map(renderProductCard)}
                    </div>
                  </div>
                ))}
            </section>
          ))}

          {uncategorizedProducts.length > 0 && (
            <section className="public-menu-category-section">
              <div className="public-menu__section-title">
                <UtensilsCrossed size={18} />
                <h2>{t('menuDesign.menuItems')}</h2>
              </div>
              <div className="public-menu__products">{uncategorizedProducts.map(renderProductCard)}</div>
            </section>
          )}
        </>
      );
    }

    return (
      <section className="public-menu__section">
        <div className="public-menu__section-title">
          <UtensilsCrossed size={18} />
          <h2>{t('menuDesign.menuItems')}</h2>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="public-menu__empty">
            <UtensilsCrossed size={28} />
            <p>{t('menuDesign.noProducts')}</p>
          </div>
        ) : (
          <div className="public-menu__products">{filteredProducts.map(renderProductCard)}</div>
        )}
      </section>
    );
  };

  const sectionRenderers = {
    featured: renderFeaturedSection,
    categories: renderCategoryTabs,
    products: renderProductsSection,
  };

  return (
    <main ref={experienceRef} className={rootClassName} style={buildStyle}>
      <div className="public-menu__scroll-region">
      {previewScreen === 'menu' ? (
        <div className="public-menu__shell">
          <header className="public-menu__hero">
            <div>
              <p>{menuName}</p>
              <h1>{establishmentName}</h1>

              {menu?.description && <span>{menu.description}</span>}

              <div className="public-menu__restaurant-info">
                {effectiveConfig.show_restaurant_info !== false && establishment?.address && (
                  <span>
                    <MapPin size={14} />
                    {establishment.address}
                  </span>
                )}

                {effectiveConfig.show_restaurant_info !== false && establishment?.phone && (
                  <a href={`tel:${establishment.phone}`}>
                    <Phone size={14} />
                    {establishment.phone}
                  </a>
                )}

                {isOrderingEnabled && effectiveConfig.show_ordering_badge !== false && (
                  <span className="is-ordering-on">
                    <ShoppingBag size={14} />
                    {translateFallback(t, 'publicMenu.ordering.enabled', 'Online ordering')}
                  </span>
                )}
              </div>

              <div className="public-menu__hero-actions">
                <LanguagePicker
                  language={language}
                  options={enabledLanguageOptions}
                  onLanguageChange={changeActiveLanguage}
                  compact={phonePreview}
                />

                {isOrderingEnabled && (
                  <>
                    <button type="button" className="public-menu-action-button public-menu-action-button--primary" onClick={handleCheckoutClick}>
                      <ShoppingBag size={16} />
                      {translateFallback(t, 'publicMenu.cart.checkout', 'Order')}
                      {Number(effectiveCartCount) > 0 && <strong>{effectiveCartCount}</strong>}
                    </button>

                    <button type="button" className="public-menu-action-button" onClick={handleOrdersClick}>
                      {translateFallback(t, 'publicMenu.customerOrders.title', 'My orders')}
                    </button>
                  </>
                )}
              </div>
            </div>

            <span className="public-menu__logo">
              {establishment?.logo ? <img src={resolveMediaUrl(establishment.logo)} alt={establishmentName} /> : <UtensilsCrossed size={28} />}
            </span>
          </header>

          {effectiveConfig.show_search !== false && (
            <label className="public-menu__search">
              <Search size={18} />
              <input
                type="search"
                value={searchTerm}
                placeholder={t('menuDesign.customerSearchPlaceholder')}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>
          )}

          {normalizedSectionOrder.map((section) => {
            const renderer = sectionRenderers[section];
            return renderer ? <div key={section}>{renderer()}</div> : null;
          })}
        </div>
      ) : null}

      {previewScreen === 'checkout' && (
        <PreviewScreen title={translateFallback(t, 'publicMenu.cart.checkout', 'Checkout')} onBack={() => setPreviewScreen('menu')}>
          <div className="public-menu-preview-screen__card">
            <ShoppingBag size={26} />
            <strong>{effectiveCartCount} items</strong>
            <p>{formatPrice(effectiveCartTotal, currency) || formatPrice(0, currency)}</p>
            <small>This is a safe customization preview. Real checkout stays on the public page.</small>
          </div>
        </PreviewScreen>
      )}

      {previewScreen === 'orders' && (
        <PreviewScreen title={translateFallback(t, 'publicMenu.customerOrders.title', 'My orders')} onBack={() => setPreviewScreen('menu')}>
          <div className="public-menu-preview-screen__card">
            <MessageCircle size={26} />
            <strong>Preview orders</strong>
            <p>Customers will see their submitted orders here.</p>
          </div>
        </PreviewScreen>
      )}

      {showFloatingCart && isOrderingEnabled && Number(effectiveCartCount) > 0 && (
        <button type="button" className="public-menu-floating-cart" onClick={handleCheckoutClick}>
          <ShoppingBag size={18} />
          <span>
            {effectiveCartCount} · {formatPrice(effectiveCartTotal, currency)}
          </span>
        </button>
      )}
      </div>


      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          establishment={establishment}
          language={language}
          fallbackLanguage={fallbackLanguage}
          t={t}
          liked={effectiveLikedProductIds.includes(Number(selectedProduct.id))}
          detailsUrl={previewMode ? '' : detailsUrlForProduct?.(selectedProduct)}
          detailsLabel={translateFallback(t, 'publicMenu.productModal.fullDetails', 'Voir tous les détails')}
          onClose={(target) => {
            setSelectedProduct(null);
            if (target === 'details' && previewMode) {
              showNotice(translateFallback(t, 'publicMenu.productModal.fullDetails', 'Voir tous les détails'));
            }
          }}
          onLike={handleLike}
          onReview={previewMode ? undefined : onReview}
          onOrderNow={handleOrderNow}
          orderingEnabled={isOrderingEnabled}
          previewMode={previewMode}
        />
      )}

      {notice && (
        <div className="public-menu-experience-notice" role="status">
          {notice}
        </div>
      )}
    </main>
  );
}